import React, {
	type RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common/recent-searches';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';
import type { UseInputIntentResult } from '@atlassian/search-intent-detection/use-input-intent';

import { ApplicationModes } from '../../../common/constants/application-modes';
import { PrimaryProductKeys } from '../../../common/constants/primary-product-keys';
import {
	QUICK_FIND_CLOSE_INTERACTION,
	QUICK_FIND_OPEN_INTERACTION,
} from '../../../common/constants/quick-find';
import type { SearchQueryStringParams } from '../../../common/constants/schemas/query-params';
import { useAppContext } from '../../../common/ui/app-context';
import {
	type QuickFindFilterPresentation,
	Textfield as DefaultTextfield,
	type TextfieldProps,
} from '../../../common/ui/quick-find/textfield';
import { useInputSkeleton } from '../../../common/ui/quick-find/textfield/input-skeleton';
import { serveQuickFindInstantSearch } from '../../../common/utils/experiments';
import { serveChatConvergenceExperiment } from '../../../common/utils/experiments/serve-chat-convergence-experiment';
import { serve3PGhostTextExperiment } from '../../../common/utils/experiments/serve3-p-ghost-text-experiment';
import { INTENT_DETECTION, isJqlQueryDetected } from '../../../common/utils/intent-detection';
import { isOnJiraFullPageSearch } from '../../../common/utils/is-on-jira-full-page-search';
import { parseGhostTextData } from '../../../common/utils/quick-find/ghost-text';
import {
	getWorkItemPageUrl,
	getWorkItemPrefetchUrls,
} from '../../../common/utils/quick-find/jira-work-item-navigation';
import { useLogException } from '../../../controllers/error-boundary/utils';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import { useQuickFindPerceivedLatency } from '../../../controllers/quick-find/perceived-latency-context';
import { getQueryWithoutSelectedQueryFilters } from '../../../controllers/quick-find/query-filters/get-query-without-selected-query-filters';
import { useCounterfactualIntentDecision } from '../../../controllers/quick-find/use-counterfactual-intent-decision';
import { useIntentDetectionVitalStats } from '../../../controllers/quick-find/use-intent-detection-vitalstats';
import { useQuickFindPostQueryVitalStats } from '../../../controllers/quick-find/use-quickfind-post-query-vitalstats';
import { useCloudId, useOrgId } from '../../../controllers/store/bootstrap';
import {
	useApplicationMode,
	useAvailableProducts,
	useIsConfigLoaded,
	usePrimaryProduct,
	useSmartLinkUsage,
	useThirdPartyConfigs,
	useUser,
} from '../../../controllers/store/bootstrap';
import {
	useQuickFindActions,
	useQuickFindOpen,
	useQuickFindQuery,
	useSelectedQueryFilters,
	useUpdateRecentQueries,
} from '../../../controllers/store/quick-find';
import { useSearchActions, useSelectedProduct } from '../../../controllers/store/search';
import { useQuickFindInputAnalytics } from '../input-analytics';
import { generateSerializedGhostText, isEmptyGhostText } from './ghost-text/ghost-text';
import { useQueryFilters } from './query-filters';
import { useDebouncedPrefetchCallback } from './utils';

export type SearchTextFieldProps = {
	dialogId: string;
	inputRef?: RefObject<HTMLInputElement>;
	localQuery: string;
	setLocalQuery: React.Dispatch<React.SetStateAction<string>>;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
	postQueryDisabled?: boolean;
	customPlaceholder?: string;
	onGhostTextGenerated?: (ghostText: string) => void;
	intentRouterEnabled?: boolean;
	filterPresentation?: QuickFindFilterPresentation;
	/** Predicted advanced-search URL emitted after the typing-debounce window; wire to `prefetchRoute`. Gated on `quick_find_typing_debounce_prefetch`. */
	onPrefetch?: (url: string) => void;
	counterfactualIntentDetection?: UseInputIntentResult;
	/** Focus the input once when this changes from false to true. */
	autoFocusInput?: boolean;
};

export const SearchTextField = ({
	dialogId,
	inputRef,
	localQuery,
	setLocalQuery,
	textfieldComponent,
	postQueryDisabled,
	customPlaceholder,
	onGhostTextGenerated,
	intentRouterEnabled,
	filterPresentation,
	onPrefetch,
	counterfactualIntentDetection,
	autoFocusInput = false,
}: SearchTextFieldProps): React.JSX.Element => {
	const previousTextQueryParam = useRef<SearchQueryStringParams['text']>();
	const hasAppliedAutoFocus = useRef(false);
	const wasQuickFindOpen = useRef(false);

	const { generateSearchUrl, onNavigate, queryParams, isNav4Enabled, isAdminHubAIEnabled } =
		useAppContext();
	const { formatMessage, locale } = useIntl();

	const applicationMode = useApplicationMode();
	const [primaryProduct] = usePrimaryProduct();
	const [user] = useUser();
	const [thirdPartyConfigs] = useThirdPartyConfigs();
	const products = useAvailableProducts();
	const quickFindOpen = useQuickFindOpen();
	const quickFindQuery = useQuickFindQuery();
	// Do not deconstruct useKeyboardHighlightProvider as it causes the SSR to fail
	const keyboardHighlight = useKeyboardHighlightProvider();
	const getSelectedIndex = keyboardHighlight?.getSelectedIndex;
	const keyboardNavComponents = keyboardHighlight?.keyboardNavComponents;
	const updateRecentQueries = useUpdateRecentQueries();

	const [selectedIndex, setSelectedIndex] = useState(getSelectedIndex?.());
	// Compute the activeDescendantId for aria-activedescendant
	const activeDescendantId = useMemo(() => {
		if (selectedIndex === undefined || !keyboardNavComponents) {
			return undefined;
		}
		const selectedComponent = keyboardNavComponents[selectedIndex];
		return selectedComponent?.navigationTrackingId || undefined;
	}, [selectedIndex, keyboardNavComponents]);

	const selectedQueryFilters = useSelectedQueryFilters();
	const [selectedProduct] = useSelectedProduct();
	const {
		selectedFilters: { jiraFilters },
	} = useSelectedFilters({});
	const { setQuickFindOpen, resetSelectedQueryFilters } = useQuickFindActions();
	const { setQueryUpdatedTime, clearAllFilters, setInputQuery } = useSearchActions();

	const {
		fireOnSearchDialogInputClicked,
		debouncedOnTextEnteredAnalyticsCallback,
		trackSearchSubmit,
		fireOnQuickFindClearButtonClicked,
		fireOnQuickFindConnectAppsPillClicked,
		fireOnGhostTextShownEvent,
	} = useQuickFindInputAnalytics();
	const { recordQueryChange, resetOnClear, resetOnSubmit } = useQuickFindPerceivedLatency();
	const fireCounterfactualIntentDecision = useCounterfactualIntentDecision(
		counterfactualIntentDetection,
	);

	const [debouncedPrefetchCallback, cancelDebouncedPrefetchCallback] = useDebouncedPrefetchCallback(
		{
			onPrefetch,
			getQuery: useCallback(() => inputRef?.current?.value ?? '', [inputRef]),
			getUrls: useCallback(
				(query: string) => {
					try {
						return getWorkItemPrefetchUrls({
							query,
							jiraFilters,
							generateSearchUrl,
							queryParams,
							isQueryJql: isJqlQueryDetected(query),
						});
					} catch {
						return [];
					}
				},
				[jiraFilters, generateSearchUrl, queryParams],
			),
		},
	);

	const logException = useLogException();

	const { abortTask, isTaskActive } = useQuickFindPostQueryVitalStats();
	const { abortTask: abortIntentDetectionTask, isTaskActive: isIntentDetectionTaskActive } =
		useIntentDetectionVitalStats();
	const isConfigLoaded = useIsConfigLoaded();

	useInputSkeleton({
		localQuery,
		inputRef,
	});

	// Ghost text is scoped to Confluence only. This replaces the `quickfind-ghosttext` feature gate,
	// which was only ever enabled for Confluence.
	const isConfluence = primaryProduct === PrimaryProductKeys.Confluence;

	const serve3PGhostText = useMemo(
		() => isConfluence && serve3PGhostTextExperiment({ thirdPartyConfigs }),
		[isConfluence, thirdPartyConfigs],
	);

	const existingGhostTextData = useMemo(
		() => (isConfluence ? parseGhostTextData(customPlaceholder) : undefined),
		[isConfluence, customPlaceholder],
	);

	// Track last persisted ghost text to prevent overwriting valid data with empty
	const lastPersistedGhostTextRef = useRef<string>('{}');

	useEffect(() => {
		if (!onGhostTextGenerated || !isConfigLoaded) {
			return;
		}

		const serialized = generateSerializedGhostText({
			serve3PGhostText,
			lastPersisted: lastPersistedGhostTextRef.current,
			products,
			thirdPartyConfigs,
			locale,
			primaryProduct,
			formatMessage,
			existingGhostTextData,
		});

		if (serialized === null || (isConfluence && isEmptyGhostText(serialized))) {
			return;
		}

		lastPersistedGhostTextRef.current = serialized;
		onGhostTextGenerated(serialized);
	}, [
		serve3PGhostText,
		products,
		thirdPartyConfigs,
		primaryProduct,
		formatMessage,
		existingGhostTextData,
		onGhostTextGenerated,
		isConfigLoaded,
		isConfluence,
		locale,
	]);

	useEffect(() => {
		if (serve3PGhostText && existingGhostTextData) {
			fireOnGhostTextShownEvent({
				id: existingGhostTextData.id,
				locale: existingGhostTextData.locale,
				customGhostText: existingGhostTextData.text,
			});
		}
	}, [serve3PGhostText, existingGhostTextData, fireOnGhostTextShownEvent]);

	/**
	 * When Quick Find is SSR'd, users may type into the input before the
	 * React component is mounted. Given that useLayoutEffect is called before
	 * the first browser paint but after the input ref is set, this essentially
	 * initialises the React component with the input's state prior to hydration.
	 * - Input in the DOM element is reflected in localQuery
	 * - Focus state is preserved as hydration causes the input to lose focus
	 *
	 * It should be noted that re-renders caused state by updates in useLayoutEffect
	 * are synchronous, i.e. occurs before the paint, hence this is TTVC friendly.
	 *
	 * When this component is not SSR'd, this effectively does nothing.
	 */
	useLayoutEffect(() => {
		if (inputRef?.current) {
			setLocalQuery(inputRef.current.value);

			const document = getDocument();
			const isFocused = document && document.activeElement === inputRef.current;
			const selectionStart = isFocused ? inputRef.current.selectionStart : null;
			const selectionEnd = isFocused ? inputRef.current.selectionEnd : null;

			if (isFocused) {
				requestAnimationFrame(() => {
					inputRef?.current?.focus();
					if (selectionStart !== null && selectionEnd !== null) {
						inputRef?.current?.setSelectionRange(selectionStart, selectionEnd);
					}
				});
			}
		}
	}, [inputRef, setLocalQuery]);

	useQueryFilters(localQuery, postQueryDisabled);

	// Only when the text query param changes, update the local query. If the
	// local query without selected query filters is the same as text query param,
	// do nothing.
	useEffect(() => {
		const textParam = typeof queryParams.text === 'string' ? queryParams.text : undefined;

		if (textParam === previousTextQueryParam.current) {
			return;
		}

		previousTextQueryParam.current = textParam;

		const localQueryWithoutSelectedQueryFilters = getQueryWithoutSelectedQueryFilters(
			localQuery,
			selectedQueryFilters,
		);

		const newLocalQuery = typeof queryParams.text === 'string' ? queryParams.text : '';

		if (localQueryWithoutSelectedQueryFilters !== newLocalQuery) {
			setInputQuery(newLocalQuery);
			setLocalQuery(newLocalQuery);
			clearAllFilters();
			resetSelectedQueryFilters();
		}
	}, [
		clearAllFilters,
		queryParams.text,
		resetSelectedQueryFilters,
		selectedQueryFilters,
		setLocalQuery,
		localQuery,
		setInputQuery,
	]);

	// Update query time when query changes
	useEffect(() => {
		setQueryUpdatedTime();
	}, [setQueryUpdatedTime, localQuery]);

	useEffect(() => {
		const input = inputRef?.current;

		// Preserve the original Quick Find focus behavior unless a host opts into autofocus.
		if (!autoFocusInput) {
			hasAppliedAutoFocus.current = false;
			wasQuickFindOpen.current = false;

			if (quickFindOpen) {
				input?.focus();
			} else {
				input?.blur();
			}
			return;
		}

		if (quickFindOpen) {
			wasQuickFindOpen.current = true;
			input?.focus();
			return;
		}

		if (wasQuickFindOpen.current) {
			wasQuickFindOpen.current = false;
			input?.blur();
			return;
		}

		if (!hasAppliedAutoFocus.current && input) {
			hasAppliedAutoFocus.current = true;
			const { activeElement } = input.ownerDocument;
			const modalWithFocus = activeElement?.closest('[role="dialog"], [aria-modal="true"]');

			if (!modalWithFocus || activeElement === input) {
				input.focus({ preventScroll: true });
			}
		}

		// Keep the asserted signal from undoing focus during Strict Mode's effect replay.
	}, [autoFocusInput, inputRef, quickFindOpen]);

	const openQuickFind = useCallback(
		(
			event:
				| React.KeyboardEvent<HTMLInputElement>
				| React.ChangeEvent<HTMLInputElement>
				| React.MouseEvent<HTMLInputElement>,
		) => {
			if (!quickFindOpen) {
				setQuickFindOpen(true);
				traceUFOPress(QUICK_FIND_OPEN_INTERACTION, event.timeStamp);
				addUFOCustomData({ queryLength: localQuery.length });
				if (event.type === 'click') {
					fireOnSearchDialogInputClicked();
				}
			}
		},
		[fireOnSearchDialogInputClicked, localQuery.length, setQuickFindOpen, quickFindOpen],
	);

	const onKeyDown = useCallback(
		async (event: React.KeyboardEvent<HTMLInputElement>) => {
			// ArrowDown, Enter and Space keys on input should open the dialog so keyboard users can access results.
			if ((event.key === 'ArrowDown' || event.key === 'Enter') && !quickFindOpen) {
				openQuickFind(event);
				return;
			}

			/**
			 * Ignore enter key press if a search result is highlighted as we let
			 * the keyboard highlight provider handle the event in this case.
			 */
			const resultHighlighted = getSelectedIndex && getSelectedIndex() !== undefined;

			if (event.key === 'Enter' && !resultHighlighted) {
				/**
				 * Prevent IME composition from submitting the search
				 */
				if (event.nativeEvent.isComposing) {
					event.preventDefault();
					return;
				}

				if (!primaryProduct) {
					return;
				}

				/**
				 * Use localQuery instead of quickFind query for keydown events
				 * to prevent desync between what's shown in the input and what's
				 * actually used in the callback here.
				 */
				const submitQuery = localQuery;

				/**
				 * Stop the event from propagating to prevent UFO
				 * VC90 metric from being cancelled on route change.
				 */
				event.stopPropagation();

				// Store search in recent searches
				if (submitQuery.trim() !== '' && user && user.id && primaryProduct) {
					addRecentSearch({
						value: quickFindQuery.trim(),
						userId: user.id,
						productId: primaryProduct,
						key: RECENT_SEARCH_KEY,
					});
					updateRecentQueries?.();
				}

				resetOnSubmit();
				event.currentTarget.blur();
				setQuickFindOpen(false);
				fireCounterfactualIntentDecision({ query: submitQuery });
				trackSearchSubmit({
					query: submitQuery,
					trigger: 'return',
					returnModified: event.metaKey || event.ctrlKey,
				});

				// Executing a search query on FPS search will always clear the filters in QF as FPS filters
				// is the source of truth for filter state.
				clearAllFilters();

				traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, event.timeStamp);

				const onNavigateCallback =
					typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

				if (primaryProduct === PrimaryProductKeys.Jira) {
					const ctrlKey = event.metaKey || event.ctrlKey;

					// Intent detection is ongitly enabled if the experiment is enabled
					if (intentRouterEnabled) {
						// VitalStats: Abort intent detection UX task if user submits search
						if (isIntentDetectionTaskActive()) {
							abortIntentDetectionTask('search_submitted');
						}

						const intentDetection = INTENT_DETECTION.value();

						const url =
							intentDetection?.route ||
							getWorkItemPageUrl({
								query: submitQuery,
								jiraFilters,
								generateSearchUrl,
								queryParams,
								isQueryJql: isJqlQueryDetected(submitQuery),
							});
						onNavigateCallback(url, 'push', event);
						return;
					}

					// Ctrl + Enter
					// On Jira Full Page Search, Ctrl + Enter = Go to NIN
					// On other products, Ctrl + Enter = Go to FPS
					const swapQuickFindCTAs =
						isOnJiraFullPageSearch() &&
						primaryProduct &&
						primaryProduct === PrimaryProductKeys.Jira;
					if ((ctrlKey && !swapQuickFindCTAs) || (!ctrlKey && swapQuickFindCTAs)) {
						onNavigateCallback(
							generateSearchUrl(
								{
									...queryParams,
									text: submitQuery,
								},
								false, // This is still Confluence, so won't be a secondary link
							),
							'push',
							event,
						);
						return;
					}

					try {
						const url = getWorkItemPageUrl({
							query: submitQuery,
							jiraFilters,
							generateSearchUrl,
							queryParams,
							isQueryJql: isJqlQueryDetected(submitQuery),
						});

						onNavigateCallback(url, 'push', event);
						return;
					} catch (error) {
						// If network or runtime errors occcur, follow the default redirection behaviour.
						logException(error);
					}
				}

				const searchForPrimaryProduct =
					!selectedProduct &&
					((applicationMode === ApplicationModes.Rovo && (event.metaKey || event.ctrlKey)) ||
						(applicationMode !== ApplicationModes.Rovo && !(event.metaKey || event.ctrlKey)));
				onNavigateCallback(
					generateSearchUrl(
						{
							...queryParams,
							...(searchForPrimaryProduct ? { product: primaryProduct } : {}),
							text: submitQuery,
							cql: undefined, // Clear CQL parameter when performing a regular search
						},
						false,
					),
					'push',
					event,
				);
			}

			if (event.key === 'Escape') {
				/**
				 * Prevent IME composition from closing the quick find
				 */
				if (event.nativeEvent.isComposing) {
					event.preventDefault();
					return;
				}
				event.currentTarget.blur();
				setQuickFindOpen(false);

				traceUFOPress(QUICK_FIND_CLOSE_INTERACTION, event.timeStamp);
			}

			// if arrow keys, then update the selected index
			if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
				setSelectedIndex(getSelectedIndex?.());
			}
		},
		[
			trackSearchSubmit,
			resetOnSubmit,
			fireCounterfactualIntentDecision,
			getSelectedIndex,
			primaryProduct,
			quickFindQuery,
			localQuery,
			user,
			setQuickFindOpen,
			selectedProduct,
			applicationMode,
			clearAllFilters,
			onNavigate,
			generateSearchUrl,
			queryParams,
			updateRecentQueries,
			jiraFilters,
			logException,
			intentRouterEnabled,
			isIntentDetectionTaskActive,
			abortIntentDetectionTask,
			quickFindOpen,
			openQuickFind,
		],
	);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newQuery = event.currentTarget.value;
			recordQueryChange(event.timeStamp, newQuery.length === 0);
			setLocalQuery(newQuery);
			debouncedOnTextEnteredAnalyticsCallback();
			debouncedPrefetchCallback();

			if (!quickFindOpen) {
				// open quick find when input is focused by quick find is not opened
				openQuickFind(event);
			}
		},
		[
			debouncedOnTextEnteredAnalyticsCallback,
			debouncedPrefetchCallback,
			setLocalQuery,
			quickFindOpen,
			openQuickFind,
			recordQueryChange,
		],
	);

	const onFocus = useCallback((_event: React.FocusEvent<HTMLInputElement>) => {
		// Don't do anything on focus
	}, []);

	const Textfield = textfieldComponent || DefaultTextfield;
	const [cloudId] = useCloudId();
	const [orgId] = useOrgId();
	const { chatIntentDetectionEnabled } = serveChatConvergenceExperiment(primaryProduct);
	const smartLinkUsage = useSmartLinkUsage();
	const defaultTextfieldProps = textfieldComponent ? {} : { filterPresentation };

	return (
		<Textfield
			value={localQuery}
			dialogId={dialogId}
			inputRef={inputRef}
			quickFindOpen={quickFindOpen}
			isNav4Enabled={isNav4Enabled}
			isAdminHubAIEnabled={isAdminHubAIEnabled}
			intentRouterEnabled={intentRouterEnabled}
			chatIntentDetectionEnabled={chatIntentDetectionEnabled}
			unifiedSurface={serveQuickFindInstantSearch(primaryProduct)}
			activeDescendantId={activeDescendantId}
			onFocus={onFocus}
			onKeyDown={onKeyDown}
			onChange={onChange}
			onClick={openQuickFind}
			onClear={() => {
				resetOnClear();
				fireOnQuickFindClearButtonClicked();
				cancelDebouncedPrefetchCallback();

				// Abort task if active when user clicks clear button
				// Note: We don't wrap in startTransition here because abortTask needs to
				// update state synchronously to prevent race conditions with new queries
				if (isTaskActive()) {
					abortTask('text_cleared');
				}
				// VitalStats: Abort intent detection UX task if user clears text
				if (isIntentDetectionTaskActive()) {
					abortIntentDetectionTask('text_cleared');
				}
				setLocalQuery('');
				inputRef?.current?.focus();
			}}
			onAppsClick={fireOnQuickFindConnectAppsPillClicked}
			placeholder={existingGhostTextData?.text}
			cloudId={cloudId}
			accountId={user.id ?? undefined}
			orgId={orgId}
			primaryProduct={primaryProduct}
			smartLinkUsage={smartLinkUsage}
			thirdPartyConfigs={thirdPartyConfigs}
			{...defaultTextfieldProps}
		/>
	);
};
